import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { TICKETS_PATHS } from '../../../../../../tickets/tickets';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-automatic-distribution-map-new',
  templateUrl: './automatic-distribution-map.component.html',
  styleUrls: ['./automatic-distribution-map.component.scss'],
  standalone: false,
})
export class AutomaticDistributionMapComponent implements OnChanges {
  @Input() result;
  @Input() tickets;
  @Input() isEmployeeGph: (route: any) => boolean = () => false;

  public routeLegend: any[] = [];
  public visibleRouteOrders: any[] = [];
  public visibleUnassignedOrders: any[] = [];
  public visibleRouteLines: any[] = [];
  public visibleStartMarkers: any[] = [];
  public visibleCoordinates: number[][] = [];
  public hasVisibleCoordinates = false;
  public mapRenderKey = 0;
  public TICKETS_PATHS = TICKETS_PATHS;

  private routeColors = ['#e53935', '#1e88e5', '#43a047', '#fb8c00', '#8e24aa', '#00897b', '#d81b60', '#6d4c41'];
  private mapDataCacheResult = null;
  private mapDataCacheTickets = null;
  private mapDataCache = null;
  private selectedRouteIds = {};
  private resultSignature = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.result) {
      const nextSignature = this.getResultSignature(this.result);

      if (nextSignature !== this.resultSignature) {
        this.resultSignature = nextSignature;
        this.selectedRouteIds = {};
        this.mapRenderKey++;
      }
    }

    this.mapDataCache = null;
    this.refreshVisibleMapData();
  }

  getRouteOrders = (result) => {
    return this.getMapData(result).routeOrders.filter((item) => this.isRouteVisible(item && item.routeId, result));
  };

  getUnassignedOrders = (result) => {
    return this.hasActiveRouteSelection(result) ? [] : this.getMapData(result).unassignedOrders;
  };

  hasAnyCoordinates = (result) => {
    return this.getAllCoordinates(result).length > 0;
  };

  getRouteLines = (result) => {
    return this.getMapData(result).routeLines.filter((line) => this.isRouteVisible(line && line.routeId, result));
  };

  getRouteLegend = (result) => {
    return this.getMapData(result).routeLegend;
  };

  getStartMarkers = (result) => {
    if (!this.hasActiveRouteSelection(result)) {
      return this.getMapData(result).startMarkers;
    }

    return this.getMapData(result).startMarkers
      .map((marker) => {
        const routes = (marker.routes || []).filter((route) => this.isRouteVisible(route && route.id, result));

        if (!routes.length) {
          return null;
        }

        return Object.assign({}, marker, { routes: routes });
      })
      .filter((marker) => !!marker);
  };

  getAllCoordinates = (result) => {
    const routeMarkerCoordinates = this.getRouteOrders(result)
      .concat(this.getUnassignedOrders(result))
      .map((item) => this.getCoordinates(item))
      .filter((coordinate) => !!coordinate);
    const startMarkerCoordinates = this.getStartMarkers(result).map((marker) => marker.coordinates);
    const lineCoordinates = this.getRouteLines(result).reduce((coordinates, line) => coordinates.concat(line.coordinates), []);

    return routeMarkerCoordinates.concat(startMarkerCoordinates).concat(lineCoordinates);
  };

  isRouteChecked(route): boolean {
    return !!this.selectedRouteIds[this.getSelectionRouteId(route && route.id)];
  }

  toggleRouteSelection(route): void {
    const routeId = this.getSelectionRouteId(route && route.id);

    if (this.selectedRouteIds[routeId]) {
      delete this.selectedRouteIds[routeId];
      this.mapRenderKey++;
      this.refreshVisibleMapData();
      return;
    }

    this.selectedRouteIds[routeId] = true;
    this.mapRenderKey++;
    this.refreshVisibleMapData();
  }

  private refreshVisibleMapData(): void {
    const mapData = this.getMapData(this.result);
    const hasSelection = this.hasActiveRouteSelectionInLegend(mapData.routeLegend);
    const isVisibleRoute = (routeId) => !hasSelection || !!this.selectedRouteIds[this.getSelectionRouteId(routeId)];

    this.routeLegend = mapData.routeLegend;
    this.visibleRouteOrders = mapData.routeOrders.filter((item) => isVisibleRoute(item && item.routeId));
    this.visibleUnassignedOrders = hasSelection ? [] : mapData.unassignedOrders;
    this.visibleRouteLines = mapData.routeLines.filter((line) => isVisibleRoute(line && line.routeId));
    this.visibleStartMarkers = hasSelection
      ? mapData.startMarkers
          .map((marker) => {
            const routes = (marker.routes || []).filter((route) => isVisibleRoute(route && route.id));

            return routes.length ? Object.assign({}, marker, { routes: routes }) : null;
          })
          .filter((marker) => !!marker)
      : mapData.startMarkers;

    const routeMarkerCoordinates = this.visibleRouteOrders
      .concat(this.visibleUnassignedOrders)
      .map((item) => this.getCoordinates(item))
      .filter((coordinate) => !!coordinate);
    const startMarkerCoordinates = this.visibleStartMarkers.map((marker) => marker.coordinates);
    const lineCoordinates = this.visibleRouteLines.reduce((coordinates, line) => coordinates.concat(line.coordinates), []);

    this.visibleCoordinates = routeMarkerCoordinates.concat(startMarkerCoordinates).concat(lineCoordinates);
    this.hasVisibleCoordinates = this.visibleCoordinates.length > 0;
  }

  private getMapData(result) {
    const tickets = this.tickets && this.tickets.dataFlatList;

    if (this.mapDataCache && this.mapDataCacheResult === result && this.mapDataCacheTickets === tickets) {
      return this.mapDataCache;
    }

    const routes = this.getRoutes(result).map((route) => Object.assign({}, route, {
      employee_name: (route.employee_name || 'Сотрудник') + (this.isEmployeeGph(route) ? ' [ГПХ]' : ''),
    }));
    const routeOrders = [];
    const resultData = this.getResultData(result);
    const unassignedOrders = ((resultData && resultData.unassigned_orders) || []).map((order, index) => ({
      result: result,
      route: null,
      order: order,
      orderIndex: index,
      unassigned: true,
    }));
    const lines = [];
    const routeLegend = [];
    const startMarkerMap = {};

    routes.forEach((route, routeIndex) => {
      const routeId = this.getRouteId(route, routeIndex);

      ((route && route.route) || []).forEach((order, index) => {
        routeOrders.push({
          result: result,
          route: route,
          routeId: routeId,
          order: order,
          orderIndex: index,
          unassigned: false,
        });
      });
    });

    routes.forEach((route, index) => {
      const geometryCoordinates = this.getGeometryCoordinates(route);
      const coordinates = geometryCoordinates.length ? geometryCoordinates : this.getRoutePointCoordinates(result, route);
      const id = this.getRouteId(route, index);
      const routeHasPath = ((route && route.route) || []).length > 0 || geometryCoordinates.length > 1;
      const routeLegendItem = {
        id: id,
        name: (route && route.employee_name) || 'Сотрудник',
        totalOrders: (route && route.total_orders) || ((route && route.route) || []).length,
        color: this.getRouteColor(index),
        transportIcon: this.getRouteTransportIcon(result, route),
        transportTitle: this.getRouteTransportTitle(result, route),
        route: route,
      };

      if (routeHasPath) {
        routeLegend.push(routeLegendItem);
      }

      if (routeHasPath) {
        const startCoordinates = this.getRouteStartCoordinates(result, route) || (coordinates.length ? coordinates[0] : null);

        if (startCoordinates) {
          this.addStartMarker(startMarkerMap, startCoordinates, routeLegendItem);
        }
      }

      if (coordinates.length < 2) {
        return;
      }

      lines.push({
        id: `${id}-outline`,
        routeId: id,
        coordinates: coordinates,
        strokeColor: '#ffffff',
        strokeOpacity: 0.95,
        strokeWidth: 9,
        zIndex: 10 + index * 2,
      });

      lines.push({
        id: `${id}-route`,
        routeId: id,
        coordinates: coordinates,
        strokeColor: this.getRouteColor(index),
        strokeOpacity: 0.98,
        strokeWidth: 5,
        zIndex: 11 + index * 2,
      });
    });

    const startMarkers = Object.keys(startMarkerMap).map((key, index) => {
      const marker = startMarkerMap[key];

      return Object.assign(marker, {
        id: 9900000 + index,
        selector: `automatic-distribution-start-${index}`,
      });
    });
    const markerCoordinates = routeOrders
      .concat(unassignedOrders)
      .map((item) => this.getCoordinates(item))
      .filter((coordinate) => !!coordinate);
    const startMarkerCoordinates = startMarkers.map((marker) => marker.coordinates);
    const lineCoordinates = lines.reduce((coordinates, line) => coordinates.concat(line.coordinates), []);
    const allCoordinates = markerCoordinates.concat(startMarkerCoordinates).concat(lineCoordinates);

    this.mapDataCacheResult = result;
    this.mapDataCacheTickets = tickets;
    this.mapDataCache = {
      routeOrders: routeOrders,
      unassignedOrders: unassignedOrders,
      routeLines: lines,
      routeLegend: routeLegend,
      startMarkers: startMarkers,
      allCoordinates: allCoordinates,
      hasAnyCoordinates: lines.length > 0 || markerCoordinates.length > 0 || startMarkers.length > 0,
    };

    return this.mapDataCache;
  }

  hasCoordinates = (item) => {
    return !!this.getCoordinates(item);
  };

  getOptions = (item) => {
    const order = item.order || {};
    const route = item.route || {};
    const ticket = this.getTicket(order);
    const inputAddress = this.getInputAddressByOrder(item && item.result, order);
    const orderId = this.toNumber(order.order_id);
    const selector = `automatic-distribution-ticket-${orderId || item.orderIndex}`;

    return {
      id: orderId || item.orderIndex,
      selector: selector,
      address: (ticket && ticket.hd_address) || this.getAddressTitle(inputAddress) || order.address || order.order_name || '',
      coordinates: this.getCoordinates(item),
      balloonCloseButton: false,
      iconContent: item.unassigned ? 'Не распределено' : route.employee_name,
      iconLayout: 'default#imageWithContent',
      iconUrl: item.unassigned ? 'assets/img/icon-local-request-grey.svg' : 'assets/img/icon-local-request.svg',
      iconImageSize: [26, 35],
      iconImageOffset: [-14, -24],
      iconContentOffset: [30, 5],
      iconContentLayout: `
        <div style="color: #000; position: absolute; background-color: white; padding: 5px; border-radius: 10px; font-weight: bold; white-space: nowrap;">
          $[properties.iconContent]
        </div>
      `,
      balloonContent: `
        <div style="width: 360px; min-height: 120px;">
          <div style="position: absolute; left: 0; top: 0; width: 100%;" id="${selector}"></div>
        </div>
      `,
    };
  };

  getStartMarkerOptions = (marker) => {
    const routesCount = marker && marker.routes ? marker.routes.length : 0;
    const balloonHeight = Math.max(120, 82 + routesCount * 22);

    return {
      id: marker.id,
      selector: marker.selector,
      address: 'Старт маршрута',
      coordinates: marker.coordinates,
      balloonCloseButton: false,
      iconContent: 'Старт',
      iconLayout: 'default#imageWithContent',
      iconUrl: this.getStartMarkerIcon(),
      iconImageSize: [36, 36],
      iconImageOffset: [-18, -18],
      iconContentOffset: [0, 0],
      iconContentLayout: '<div></div>',
      balloonContent: `
        <div style="width: 260px; min-height: ${balloonHeight}px;">
          <div style="position: absolute; left: 0; top: 0; width: 100%;" id="${marker.selector}"></div>
        </div>
      `,
    };
  };

  trackByRouteOrder(index: number, item): string {
    const order = item && item.order;
    return `${this.mapRenderKey}-${item && item.routeId ? item.routeId : 'none'}-${order && order.order_id ? order.order_id : index}`;
  }

  trackByRouteLine(index: number, line): string {
    return `${this.mapRenderKey}-${String((line && line.id) || index)}`;
  }

  trackByRouteLegend(index: number, item): string {
    return `${String((item && item.id) || index)}-${(item && item.transportIcon) || 'run'}`;
  }

  trackByStartMarker(index: number, item): string {
    return `${this.mapRenderKey}-${String((item && item.id) || index)}`;
  }

  getTicket(order) {
    const orderId = this.toNumber(order && order.order_id);
    const tickets = this.tickets && this.tickets.dataFlatList;

    if (!orderId || !tickets) {
      return null;
    }

    return tickets.find((ticket) => this.toNumber(ticket && ticket.id) === orderId) || null;
  }

  getOrderNumber(order): string {
    const ticket = this.getTicket(order);
    const value = (ticket && ticket.hd_ticket_number) || (order && (order.hd_ticket_number || order.order_id));

    return value ? String(value) : '-';
  }

  getOrderLink(order): string[] {
    const ticketId = order && (order.order_id || order.id);

    return ticketId ? ['/' + this.TICKETS_PATHS.ticketLink + '/' + ticketId] : null;
  }

  getCoordinates = (item): number[] | null => {
    const order = item && item.order;
    const result = item && item.result;
    const ticket = this.getTicket(order);
    const inputAddress = this.getInputAddressByOrder(result, order);
    const coordinate =
      this.getLocationCoordinates(order) ||
      this.getLocationCoordinates(order && order.address) ||
      this.getLocationCoordinates(inputAddress) ||
      this.getLocationCoordinates(ticket && ticket.extra_flags);

    return coordinate;
  };

  private getRoutes(result): any[] {
    const resultData = this.getResultData(result);

    return (resultData && resultData.routes) || [];
  }

  private getRouteColor(index: number): string {
    return this.routeColors[index % this.routeColors.length];
  }

  private getResultData(result) {
    const resultData = result && result.results && typeof result.results === 'object'
      ? result.results
      : result && result.result && typeof result.result === 'object'
      ? result.result
      : result;
    const input = (resultData && (resultData.input || resultData.inputs)) || (result && (result.input || result.inputs));

    if (resultData && input) {
      return Object.assign({}, resultData, {
        input: input,
        inputs: input,
      });
    }

    return resultData;
  }

  private getRoutePointCoordinates(result, route): number[][] {
    const coordinates = [];
    const startCoordinates = this.getRouteStartCoordinates(result, route);
    const endCoordinates = this.getRouteEndCoordinates(result, route);

    if (startCoordinates) {
      coordinates.push(startCoordinates);
    }

    ((route && route.route) || []).forEach((order, orderIndex) => {
      const orderCoordinates = this.getCoordinates({
        result: result,
        route: route,
        order: order,
        orderIndex: orderIndex,
        unassigned: false,
      });

      if (orderCoordinates) {
        coordinates.push(orderCoordinates);
      }
    });

    if (endCoordinates) {
      coordinates.push(endCoordinates);
    }

    return coordinates;
  }

  private getRouteStartCoordinates(result, route): number[] | null {
    const employee = this.getRouteEmployee(result, route);

    return this.getLocationCoordinates((route && route.start_location) || (employee && employee.start_location));
  }

  private getRouteEndCoordinates(result, route): number[] | null {
    const employee = this.getRouteEmployee(result, route);

    return this.getLocationCoordinates((route && route.end_location) || (employee && employee.end_location));
  }

  private addStartMarker(markerMap, coordinates: number[], routeLegendItem): void {
    const key = coordinates.map((coordinate) => Number(coordinate).toFixed(6)).join(',');

    if (!markerMap[key]) {
      markerMap[key] = {
        coordinates: coordinates,
        coordinateText: coordinates.join(', '),
        routes: [],
      };
    }

    markerMap[key].routes.push({
      id: routeLegendItem.id,
      name: routeLegendItem.name,
      color: routeLegendItem.color,
      totalOrders: routeLegendItem.totalOrders,
    });
  }

  private getStartMarkerIcon(): string {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
        <circle cx="18" cy="18" r="16" fill="#172b4d" stroke="#ffcc00" stroke-width="4"/>
        <text x="18" y="23" text-anchor="middle" font-family="Arial, sans-serif" font-size="16" font-weight="700" fill="#ffffff">С</text>
      </svg>
    `;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }

  private getGeometryCoordinates(route): number[][] {
    const geometry = route && route.geometry;
    const coordinates = geometry && Array.isArray(geometry.coordinates) ? geometry.coordinates : Array.isArray(geometry) ? geometry : [];
    const isGeoJson = !!(geometry && geometry.type);

    return coordinates.map((coordinate) => this.getGeometryCoordinate(coordinate, isGeoJson)).filter((coordinate) => !!coordinate);
  }

  private getGeometryCoordinate(coordinate, isGeoJson: boolean): number[] | null {
    if (!Array.isArray(coordinate) || coordinate.length < 2) {
      return null;
    }

    const first = this.toCoordinate(coordinate[0]);
    const second = this.toCoordinate(coordinate[1]);

    if (first === null || second === null) {
      return null;
    }

    if (isGeoJson) {
      return this.isValidCoordinates(second, first) ? [second, first] : null;
    }

    if (this.isValidCoordinates(first, second)) {
      return [first, second];
    }

    return this.isValidCoordinates(second, first) ? [second, first] : null;
  }

  private getLocationCoordinates(location): number[] | null {
    if (!location) {
      return null;
    }

    const latitude = this.toCoordinate(location.latitude || location.lat);
    const longitude = this.toCoordinate(location.longitude || location.lon || location.lng);

    if (!this.isValidCoordinates(latitude, longitude)) {
      return null;
    }

    return [latitude, longitude];
  }

  private getRouteEmployee(result, route) {
    const employeeId = this.toNumber(route && route.employee_id);

    if (!employeeId) {
      return null;
    }

    return this.getInputEmployees(result).find((employee) => this.toNumber(employee && (employee.id || employee.employee_id)) === employeeId) || null;
  }

  private getRouteTransportIcon(result, route): 'car' | 'run' | 'bus' {
    const transportType = this.getRouteTransportType(result, route);

    if (transportType === 'driving') {
      return 'car';
    }

    if (transportType === 'public_transport') {
      return 'bus';
    }

    return 'run';
  }

  private getRouteTransportTitle(result, route): string {
    const transportType = this.getRouteTransportType(result, route);

    if (transportType === 'driving') {
      return 'На машине';
    }

    if (transportType === 'public_transport') {
      return 'На общественном транспорте';
    }

    return 'Пешком';
  }

  private getRouteTransportType(result, route): string {
    const routeTransportType = this.normalizeTransportType(route && route.transport_type);

    if (routeTransportType) {
      return routeTransportType;
    }

    const employee = this.getRouteEmployee(result, route);
    const employeeTransportType = this.normalizeTransportType(employee && employee.transport_type);

    if (employeeTransportType) {
      return employeeTransportType;
    }

    return this.hasEmployeeCar(result, route) ? 'driving' : 'walking';
  }

  private normalizeTransportType(value): string | null {
    const transportType = value === null || value === undefined ? '' : String(value).toLowerCase();

    return ['walking', 'driving', 'public_transport'].indexOf(transportType) > -1 ? transportType : null;
  }

  private hasEmployeeCar(result, route): boolean {
    const routeAvailability = this.getCarAvailability(route);

    if (routeAvailability !== null) {
      return routeAvailability;
    }

    const employee = this.getRouteEmployee(result, route);

    return this.getCarAvailability(employee) === true;
  }

  private getCarAvailability(entity): boolean | null {
    if (!entity) {
      return null;
    }

    if (entity.availability_of_a_car !== undefined && entity.availability_of_a_car !== null) {
      return entity.availability_of_a_car === true;
    }

    const restriction = (entity.restrictions || []).find(
      (item) => item && item.availability_of_a_car !== undefined && item.availability_of_a_car !== null
    );

    return restriction ? restriction.availability_of_a_car === true : null;
  }

  private getInputOrders(result): any[] {
    const input = this.getInput(result);

    return (input && input.orders) || [];
  }

  private getInputAddresses(result): any[] {
    const input = this.getInput(result);

    return (input && input.addresses) || [];
  }

  private getInputEmployees(result): any[] {
    const input = this.getInput(result);

    return (input && input.employees) || [];
  }

  private getInput(result) {
    const resultData = this.getResultData(result);

    return (resultData && (resultData.input || resultData.inputs)) || (result && (result.input || result.inputs)) || null;
  }

  private getInputOrder(result, order) {
    const orderId = this.toNumber(order && (order.order_id || order.id));

    if (!orderId) {
      return null;
    }

    return this.getInputOrders(result).find((inputOrder) => this.toNumber(inputOrder && (inputOrder.id || inputOrder.order_id)) === orderId) || null;
  }

  private getInputAddressByOrder(result, order) {
    const inputOrder = this.getInputOrder(result, order);
    const addressId = this.toNumber((order && order.address_id) || (inputOrder && inputOrder.address_id));

    if (!addressId) {
      return null;
    }

    return this.getInputAddresses(result).find((address) => this.toNumber(address && (address.id || address.address_id)) === addressId) || null;
  }

  private getAddressTitle(address): string {
    if (!address) {
      return '';
    }

    if (address.address || address.full_address || address.name) {
      return address.address || address.full_address || address.name;
    }

    const coordinates = this.getLocationCoordinates(address);

    return coordinates ? coordinates.join(', ') : '';
  }

  private isValidCoordinates(latitude, longitude): boolean {
    return latitude !== null && longitude !== null && latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;
  }

  private toCoordinate(value): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const coordinate = Number(String(value).replace(',', '.'));

    return Number.isFinite(coordinate) ? coordinate : null;
  }

  private toNumber(value): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numberValue = Number(value);

    return Number.isFinite(numberValue) ? numberValue : null;
  }

  private getRouteId(route, index: number): string {
    const routeId = route && route.employee_id !== null && route.employee_id !== undefined ? route.employee_id : index;

    return this.getSelectionRouteId(routeId);
  }

  private getSelectionRouteId(routeId): string {
    return String(routeId);
  }

  private hasActiveRouteSelection(result): boolean {
    return this.hasActiveRouteSelectionInLegend(this.getRouteLegend(result));
  }

  private isRouteVisible(routeId, result): boolean {
    if (!this.hasActiveRouteSelection(result)) {
      return true;
    }

    return !!this.selectedRouteIds[this.getSelectionRouteId(routeId)];
  }

  private getSelectedRouteIds(): string[] {
    return Object.keys(this.selectedRouteIds).filter((routeId) => this.selectedRouteIds[routeId]);
  }

  private hasActiveRouteSelectionInLegend(routeLegend): boolean {
    const selectedIds = this.getSelectedRouteIds();

    if (!selectedIds.length) {
      return false;
    }

    return (routeLegend || []).some((route) => selectedIds.indexOf(this.getSelectionRouteId(route && route.id)) !== -1);
  }

  private getResultSignature(result): string {
    const resultData = this.getResultData(result);
    const routes = (resultData && resultData.routes) || [];
    const unassignedOrders = (resultData && resultData.unassigned_orders) || [];
    const routeSignature = routes
      .map((route, index) => {
        const routeOrders = ((route && route.route) || [])
          .map((order) =>
            [
              order && order.order_id,
              order && order.address_id,
              order && order.latitude,
              order && order.longitude,
              order && order.arrival_time,
              order && order.work_start_time,
              order && order.work_end_time,
            ].join(':')
          )
          .join('|');
        const geometry = route && route.geometry;
        const geometryCoordinates = geometry && Array.isArray(geometry.coordinates) ? geometry.coordinates : Array.isArray(geometry) ? geometry : [];

        return [
          this.getRouteId(route, index),
          route && route.employee_name,
          route && route.total_orders,
          route && route.route_start_time,
          route && route.route_end_time,
          route && route.start_of_shift,
          route && route.end_of_shift,
          this.getRouteTransportType(result, route),
          geometryCoordinates.length,
          routeOrders,
        ].join(';');
      })
      .join('||');
    const unassignedSignature = unassignedOrders.map((order) => order && order.order_id).join('|');

    return `${routeSignature}__${unassignedSignature}`;
  }
}
